import { TableBody, TableCell, TableRow } from "@windmill/react-ui";
import EditDeleteButton from "components/table/EditDeleteButton";
import React, { useState, useContext, useEffect, useRef } from "react";
import useToggleDrawer from "hooks/useToggleDrawer";
import ViewSeoVirtualModal from "components/modal/ViewSeoVirtualModal";
import SeoVirtualDrawer from "components/drawer/SeoVirtualDrawer";
import MainDrawer from "components/drawer/MainDrawer";
import DeleteModal from "components/modal/DeleteModal";
import { notifySuccess, notifyError } from "utils/toast";
import { SidebarContext } from "context/SidebarContext";
import { FiEye } from "react-icons/fi";
import Tooltip from "components/tooltip/Tooltip";
import ViewContactModal from "components/modal/ViewContactModal";
import ShowContact from "components/table/showContact";
import ShowConnection from "components/table/showConnection";
import SeoVirtualServices from "services/SeoVirtualServices";

const ConnectModal = ({ open, onClose, onSubmit }) => {
  const [reason, setReason] = useState("");

  const handleSubmit = () => {
    if (!reason.trim()) {
      notifyError("Please provide a reason");
      return;
    }
    onSubmit(reason);
    setReason("");
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-md w-[1000px] max-w-full">
        <h2 className="text-lg font-semibold mb-4">Reason for Connection</h2>
        <textarea
          className="w-full h-40 p-3 border rounded-md dark:bg-gray-700 dark:border-gray-600 resize-none"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Enter reason for connection"
        />
        <div className="flex justify-end mt-5 gap-3">
          <button
            className="px-5 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-5 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
            onClick={handleSubmit}
          >
            Connect
          </button>
        </div>
      </div>
    </div>
  );
};


const SeoVirtualTable = ({
  seodata,
  perpage,
  hideEdit,
  hideDelete,
  setOffset,
  hideView,
  tab,
}) => {
  const {
    title,
    serviceId,
    handleModalOpen,
    handleUpdate,
    handleView,
    isViewMode,
  } = useToggleDrawer();
  const { setIsUpdate } = useContext(SidebarContext);

  const [showActiveModal, setShowActiveModal] = useState(false);
  const [activeIndex] = useState(null);
  const [status] = useState(false);
  const tableRef = useRef(null); // Reference for the table container
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [contactId, setContactId] = useState(null);
  const [tableData, setTableData] = useState(0);
  const [isViewOpen, setIsViewOpen] = useState(false);


  useEffect(() => {
    // console.log("SeoVirtualTable → seodata length:", seodata?.length);
    // console.table(seodata);
    setTableData(seodata);
  }, [seodata]);

  useEffect(() => {
    let timeout;
    const handleWheel = (event) => {
      // Check if the scroll is inside the table container

      if (tableRef.current && tableRef.current.contains(event.target)) {
        if (timeout) return;
        if (event.shiftKey) {
          return;
        }
        timeout = setTimeout(() => {
          setOffset((prevOffset) => prevOffset + 100);
          timeout = null;
        }, 200);
      }
    };

    // Attach event listener to the table container instead of the window
    const tableElement = tableRef.current;
    if (tableElement) {
      tableElement.addEventListener("wheel", handleWheel);
    }

    return () => {
      if (tableElement) {
        tableElement.removeEventListener("wheel", handleWheel);
      }
    };
  }, [setOffset]);

  const handleViewResume = (resumeUrl) => {
    if (resumeUrl) {
      const fileUrl = `${resumeUrl}`;
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleConnect = (id, index) => {
    setSelectedUserId(id);
    setSelectedRowIndex(index);
    setIsConnectModalOpen(true);
  };

  const submitConnection = async (reason) => {
    try {
      await SeoVirtualServices.updateContactStatus(selectedUserId, reason);

      notifySuccess("Contact marked as connected");
      seodata[selectedRowIndex].is_connected = 1;
      setIsUpdate(true);
      setIsConnectModalOpen(false);
    } catch (err) {
      notifyError("Failed to connect");
    }
  };

  return (
    <>
      <ConnectModal
        open={isConnectModalOpen}
        onClose={() => setIsConnectModalOpen(false)}
        onSubmit={submitConnection}
      />

      <DeleteModal id={serviceId} title={title} />

      <MainDrawer>
        <SeoVirtualDrawer id={serviceId} isViewMode={isViewMode} />
      </MainDrawer>
      {tab === 0 && hideView && (
        <ViewSeoVirtualModal id={serviceId} title={title} data={seodata} />
      )}
      {tab === 1 && hideView && contactId && (
        <ViewContactModal
          key={contactId}
          id={contactId}
          title="Contact Details"
        />
      )}

      <TableBody>
        {seodata?.slice(0, perpage).map((data, index) => (
          <TableRow key={`${data.id}`}>
            {tab === 0 ? (
              <>
                <TableCell className="text-center text-sm">
                  {index + 1}
                </TableCell>
                <TableCell
                  className="text-center text-sm"
                  style={{
                    maxWidth: "250px",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  <p
                    onClick={() => handleViewResume(data["url"])}
                    className="hover:text-blue-500 hover:underline cursor-pointer"
                  >
                    {data["url"]}
                  </p>
                </TableCell>
                <TableCell
                  className="text-center text-sm"
                  style={{
                    maxWidth: "250px",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  {data["keywords"]}
                </TableCell>
                <TableCell
                  className="text-center text-sm"
                  style={{
                    maxWidth: "250px",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  {data["title"]}
                </TableCell>
                <TableCell>
                  <div className="flex justify-center text-right">
                    <div className="p-2 cursor-pointer text-gray-400 hover:text-green-600">
                      <button
                        onClick={() =>
                          handleView(data.id, "SEO Virtual Details")
                        }
                        className="p-2 cursor-pointer text-gray-400 hover:text-red-600 focus:outline-none"
                      >
                        <Tooltip
                          id={data.id}
                          Icon={FiEye}
                          title="ViewOrder"
                          bgColor="#34D399"
                        />
                      </button>
                    </div>
                    <EditDeleteButton
                      title={"Seo Virtual"}
                      id={data.id}
                      handleUpdate={handleUpdate}
                      handleModalOpen={handleModalOpen}
                      hideEdit={hideEdit}
                      hideDelete={hideDelete}
                    />
                  </div>
                </TableCell>
              </>
            ) : (
              <>
                <TableCell className="text-center text-sm">
                  {index + 1}
                </TableCell>
                <TableCell
                  className="text-center text-sm"
                  style={{
                    maxWidth: "250px",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  {data?.name ||
                    `${data?.firstname || ""} ${data?.lastname || ""}`.trim() ||
                    "-"}
                </TableCell>
                <TableCell
                  className="text-center text-sm"
                  style={{
                    maxWidth: "250px",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  {data["email"]}
                </TableCell>
                <TableCell
                  className="text-center text-sm"
                  style={{
                    maxWidth: "250px",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  {data["phone"]}
                </TableCell>
                <TableCell
                  className="text-center text-sm"
                  style={{
                    maxWidth: "250px",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  {data["company_name"] || "-"}
                </TableCell>
                <TableCell className="text-center text-sm">
                  {data?.created_at
                    ? new Date(data.created_at).toLocaleDateString("en-GB")
                    : "-"}
                </TableCell>

                {/* <TableCell className="text-center">
                  <div className="flex flex-col items-center justify-center">
                    <ShowConnection
                      id={data.id}
                      active={data.is_connected === true}
                      title_active="Contact"
                      handleConnect={() => handleConnect(data.id, index)}
                    />
                  </div>
                </TableCell> */}
                <TableCell className="text-center">
                  <div className="flex flex-col items-center justify-center">
                    {data.is_read ? (
                      <p className="text-green-600 text-sm">Read</p>
                    ) : (
                      <p className="text-red-600 text-sm">Unread</p>
                    )}
                  </div>
                </TableCell>



                <TableCell>
                  <div className="flex justify-center text-right">
                    <div className="p-2 cursor-pointer text-gray-400 hover:text-green-600">

                      <button
                        onClick={() => {
                          setContactId(data.id);
                          handleView(data.id, "Contact Details");
                          setIsViewOpen(true);

                          setTableData(prev =>
                            prev.map(item =>
                              item.id === data.id
                                ? { ...item, is_read: true }
                                : item
                            )
                          );
                        }}
                        className="p-2 cursor-pointer text-gray-400 hover:text-red-600 focus:outline-none"
                      >
                        <Tooltip
                          id={data.id}
                          Icon={FiEye}
                          title="ViewOrder"
                          bgColor="#34D399"
                        />
                      </button>

                    </div>
                  </div>
                </TableCell>
              </>
            )}
          </TableRow>
        ))}
      </TableBody>
    </>
  );
};

export default SeoVirtualTable;
